import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {atom, useRecoilState, useRecoilValue} from 'recoil';
import {
  ECollapse,
  ESmallButton,
  EUndecoratedButton,
} from 'webapp-lib/Components';
import {getLocalStorageSideEffect} from 'webapp-lib/utils';

import {userState} from '_/state/user';

type CollapseStore = {
  [key: string]: boolean;
};

const savedCollapseState = atom<CollapseStore>({
  key: 'ESavedCollapse',
  default: {},
  effects: [getLocalStorageSideEffect('esavedcollapse')],
});

export const ESavedCollapse = ({
  viewId,
  children,
  displayUndecoratedButton,
  testId,
}: {
  viewId: string;
  children: React.ReactElement;
  displayUndecoratedButton?: boolean;
  testId?: string;
}): React.ReactElement => {
  const {t} = useTranslation('common');
  const user = useRecoilValue(userState);
  const [collapseState, setCollapseState] = useRecoilState(savedCollapseState);
  const storeId = `${user.userId}_${viewId}`;

  const isOpen = collapseState[storeId] || false;

  const handleButtonClick = useCallback(() => {
    setCollapseState({...collapseState, [storeId]: !isOpen});
  }, [collapseState, isOpen, setCollapseState, storeId]);

  return (
    <>
      {displayUndecoratedButton ? (
        <EUndecoratedButton
          data-testid={
            testId ? `EUndecoratedButton-ESavedCollapse-${testId}` : undefined
          }
          onClick={handleButtonClick}
        >
          {isOpen ? t('actions.showLess') : t('actions.showMore')}
        </EUndecoratedButton>
      ) : (
        <ESmallButton
          testId={testId ? `ESavedCollapse-${testId}` : undefined}
          onClick={handleButtonClick}
        >
          {isOpen ? t('actions.showLess') : t('actions.showMore')}
        </ESmallButton>
      )}
      <ECollapse in={isOpen} mountOnEnter unmountOnExit>
        {children}
      </ECollapse>
    </>
  );
};
